/**
 * A cloud region: `label` is its short name (Sydney), `name` its full name where
 * one is set (Asia Pacific (Sydney)), and `key` its code (ap-southeast-2).
 */
type Region = { provider: string; label: string; name?: string; key: string };
type ProviderRegionMap = Record<string, Region>;

export const REGION_MAP: Record<string, ProviderRegionMap> = {
  AWS: {
    'us-east-1': {
      provider: 'AWS',
      label: 'N. Virginia',
      name: 'US East (N. Virginia)',
      key: 'us-east-1',
    },
    'us-east-2': {
      provider: 'AWS',
      label: 'Ohio',
      name: 'US East (Ohio)',
      key: 'us-east-2',
    },
    'us-west-1': {
      provider: 'AWS',
      label: 'N. California',
      name: 'US West (N. California)',
      key: 'us-west-1',
    },
    'us-west-2': {
      provider: 'AWS',
      label: 'Oregon',
      name: 'US West (Oregon)',
      key: 'us-west-2',
    },
    'us-gov-west-1': {
      provider: 'AWS',
      label: 'GovCloud US-West',
      name: 'AWS GovCloud (US)',
      key: 'us-gov-west-1',
    },
    'us-gov-east-1': {
      provider: 'AWS',
      label: 'GovCloud US-East',
      name: 'AWS GovCloud (US-East)',
      key: 'us-gov-east-1',
    },
    'ca-central-1': {
      provider: 'AWS',
      label: 'Central',
      name: 'Canada (Central)',
      key: 'ca-central-1',
    },
    'ca-west-1': {
      provider: 'AWS',
      label: 'Calgary',
      name: 'Canada West (Calgary)',
      key: 'ca-west-1',
    },
    'mx-central-1': {
      provider: 'AWS',
      label: 'Mexico',
      name: 'Mexico (Central)',
      key: 'mx-central-1',
    },
    'eu-north-1': {
      provider: 'AWS',
      label: 'Stockholm',
      name: 'EU (Stockholm)',
      key: 'eu-north-1',
    },
    'eu-west-1': {
      provider: 'AWS',
      label: 'Ireland',
      name: 'EU (Ireland)',
      key: 'eu-west-1',
    },
    'eu-west-2': {
      provider: 'AWS',
      label: 'London',
      name: 'EU (London)',
      key: 'eu-west-2',
    },
    'eu-west-3': {
      provider: 'AWS',
      label: 'Paris',
      name: 'EU (Paris)',
      key: 'eu-west-3',
    },
    'eu-central-1': {
      provider: 'AWS',
      label: 'Frankfurt',
      name: 'EU (Frankfurt)',
      key: 'eu-central-1',
    },
    'eu-central-2': {
      provider: 'AWS',
      label: 'Zurich',
      name: 'EU (Zurich)',
      key: 'eu-central-2',
    },
    'eu-south-1': {
      provider: 'AWS',
      label: 'Milan',
      name: 'EU (Milan)',
      key: 'eu-south-1',
    },
    'eu-south-2': {
      provider: 'AWS',
      label: 'Spain',
      name: 'EU (Spain)',
      key: 'eu-south-2',
    },
    'af-south-1': {
      provider: 'AWS',
      label: 'Cape Town',
      name: 'Africa (Cape Town)',
      key: 'af-south-1',
    },
    'ap-northeast-1': {
      provider: 'AWS',
      label: 'Tokyo',
      name: 'Asia Pacific (Tokyo)',
      key: 'ap-northeast-1',
    },
    'ap-northeast-2': {
      provider: 'AWS',
      label: 'Seoul',
      name: 'Asia Pacific (Seoul)',
      key: 'ap-northeast-2',
    },
    'ap-northeast-3': {
      provider: 'AWS',
      label: 'Osaka',
      name: 'Asia Pacific (Osaka)',
      key: 'ap-northeast-3',
    },
    'ap-southeast-1': {
      provider: 'AWS',
      label: 'Singapore',
      name: 'Asia Pacific (Singapore)',
      key: 'ap-southeast-1',
    },
    'ap-southeast-2': {
      provider: 'AWS',
      label: 'Sydney',
      name: 'Asia Pacific (Sydney)',
      key: 'ap-southeast-2',
    },
    'ap-southeast-3': {
      provider: 'AWS',
      label: 'Jakarta',
      name: 'Asia Pacific (Jakarta)',
      key: 'ap-southeast-3',
    },
    'ap-southeast-4': {
      provider: 'AWS',
      label: 'Melbourne',
      name: 'Asia Pacific (Melbourne)',
      key: 'ap-southeast-4',
    },
    'ap-southeast-5': {
      provider: 'AWS',
      label: 'Malaysia',
      name: 'Asia Pacific (Malaysia)',
      key: 'ap-southeast-5',
    },
    'ap-southeast-6': {
      provider: 'AWS',
      label: 'New Zealand',
      name: 'Asia Pacific (New Zealand)',
      key: 'ap-southeast-6',
    },
    'ap-southeast-7': {
      provider: 'AWS',
      label: 'Thailand',
      name: 'Asia Pacific (Thailand)',
      key: 'ap-southeast-7',
    },
    'ap-east-1': {
      provider: 'AWS',
      label: 'Hong Kong',
      name: 'Asia Pacific (Hong Kong)',
      key: 'ap-east-1',
    },
    'ap-east-2': {
      provider: 'AWS',
      label: 'Taipei',
      name: 'Asia Pacific (Taipei)',
      key: 'ap-east-2',
    },
    'ap-south-1': {
      provider: 'AWS',
      label: 'Mumbai',
      name: 'Asia Pacific (Mumbai)',
      key: 'ap-south-1',
    },
    'ap-south-2': {
      provider: 'AWS',
      label: 'Hyderabad',
      name: 'Asia Pacific (Hyderabad)',
      key: 'ap-south-2',
    },
    'sa-east-1': {
      provider: 'AWS',
      label: 'São Paulo',
      name: 'South America (São Paulo)',
      key: 'sa-east-1',
    },
    'me-south-1': {
      provider: 'AWS',
      label: 'Bahrain',
      name: 'Middle East (Bahrain)',
      key: 'me-south-1',
    },
    'me-central-1': {
      provider: 'AWS',
      label: 'UAE',
      name: 'Middle East (UAE)',
      key: 'me-central-1',
    },
    'il-central-1': {
      provider: 'AWS',
      label: 'Tel Aviv',
      name: 'Israel (Tel Aviv)',
      key: 'il-central-1',
    },
    'cn-north-1': {
      provider: 'AWS',
      label: 'Beijing',
      name: 'China (Beijing)',
      key: 'cn-north-1',
    },
    'cn-northwest-1': {
      provider: 'AWS',
      label: 'Ningxia',
      name: 'China (Ningxia)',
      key: 'cn-northwest-1',
    },
  },

  GCP: {
    'us-west1': { provider: 'GCP', label: 'Oregon', key: 'us-west1' },
    'us-west2': { provider: 'GCP', label: 'Los Angeles', key: 'us-west2' },
    'us-central1': { provider: 'GCP', label: 'Iowa', key: 'us-central1' },
    'us-east1': { provider: 'GCP', label: 'South Carolina', key: 'us-east1' },
    'us-east4': {
      provider: 'GCP',
      label: 'Northern Virginia',
      key: 'us-east4',
    },
    'southamerica-east1': {
      provider: 'GCP',
      label: 'São Paulo',
      key: 'southamerica-east1',
    },
    'europe-west1': { provider: 'GCP', label: 'Belgium', key: 'europe-west1' },
    'europe-west2': { provider: 'GCP', label: 'London', key: 'europe-west2' },
    'europe-west3': {
      provider: 'GCP',
      label: 'Frankfurt',
      key: 'europe-west3',
    },
    'europe-west4': {
      provider: 'GCP',
      label: 'Netherlands',
      key: 'europe-west4',
    },
    'europe-north1': {
      provider: 'GCP',
      label: 'Finland',
      key: 'europe-north1',
    },
    'asia-east1': { provider: 'GCP', label: 'Taiwan', key: 'asia-east1' },
    'asia-northeast1': {
      provider: 'GCP',
      label: 'Tokyo',
      key: 'asia-northeast1',
    },
    'asia-northeast2': {
      provider: 'GCP',
      label: 'Osaka',
      key: 'asia-northeast2',
    },
    'asia-northeast3': {
      provider: 'GCP',
      label: 'Seoul',
      key: 'asia-northeast3',
    },
    'asia-south1': { provider: 'GCP', label: 'Mumbai', key: 'asia-south1' },
    'asia-southeast1': {
      provider: 'GCP',
      label: 'Singapore',
      key: 'asia-southeast1',
    },
    'australia-southeast1': {
      provider: 'GCP',
      label: 'Sydney',
      key: 'australia-southeast1',
    },
    global: { provider: 'GCP', label: 'Global', key: 'global' },
  },

  Azure: {
    eastus: { provider: 'Azure', label: 'East US', key: 'eastus' },
    eastus2: { provider: 'Azure', label: 'East US 2', key: 'eastus2' },
    westus: { provider: 'Azure', label: 'West US', key: 'westus' },
    westus2: { provider: 'Azure', label: 'West US 2', key: 'westus2' },
    northcentralus: {
      provider: 'Azure',
      label: 'North Central US',
      key: 'northcentralus',
    },
    southcentralus: {
      provider: 'Azure',
      label: 'South Central US',
      key: 'southcentralus',
    },
    centralus: { provider: 'Azure', label: 'Central US', key: 'centralus' },
    northeurope: {
      provider: 'Azure',
      label: 'North Europe',
      key: 'northeurope',
    },
    westeurope: { provider: 'Azure', label: 'West Europe', key: 'westeurope' },
    eastasia: { provider: 'Azure', label: 'East Asia', key: 'eastasia' },
    southeastasia: {
      provider: 'Azure',
      label: 'Southeast Asia',
      key: 'southeastasia',
    },
    japaneast: { provider: 'Azure', label: 'Japan East', key: 'japaneast' },
    japanwest: { provider: 'Azure', label: 'Japan West', key: 'japanwest' },
    australiaeast: {
      provider: 'Azure',
      label: 'Australia East',
      key: 'australiaeast',
    },
    australiasoutheast: {
      provider: 'Azure',
      label: 'Australia Southeast',
      key: 'australiasoutheast',
    },
    southindia: { provider: 'Azure', label: 'South India', key: 'southindia' },
    centralindia: {
      provider: 'Azure',
      label: 'Central India',
      key: 'centralindia',
    },
    westindia: { provider: 'Azure', label: 'West India', key: 'westindia' },
    canadacentral: {
      provider: 'Azure',
      label: 'Canada Central',
      key: 'canadacentral',
    },
    canadaeast: { provider: 'Azure', label: 'Canada East', key: 'canadaeast' },
    uksouth: { provider: 'Azure', label: 'UK South', key: 'uksouth' },
    ukwest: { provider: 'Azure', label: 'UK West', key: 'ukwest' },
    francecentral: {
      provider: 'Azure',
      label: 'France Central',
      key: 'francecentral',
    },
    francesouth: {
      provider: 'Azure',
      label: 'France South',
      key: 'francesouth',
    },
    germanynorth: {
      provider: 'Azure',
      label: 'Germany North',
      key: 'germanynorth',
    },
    germanywestcentral: {
      provider: 'Azure',
      label: 'Germany West Central',
      key: 'germanywestcentral',
    },
    southafricanorth: {
      provider: 'Azure',
      label: 'South Africa North',
      key: 'southafricanorth',
    },
    southafricawest: {
      provider: 'Azure',
      label: 'South Africa West',
      key: 'southafricawest',
    },
    uaecentral: { provider: 'Azure', label: 'UAE Central', key: 'uaecentral' },
    uaenorth: { provider: 'Azure', label: 'UAE North', key: 'uaenorth' },
    brazilsouth: {
      provider: 'Azure',
      label: 'Brazil South',
      key: 'brazilsouth',
    },
    australiacentral: {
      provider: 'Azure',
      label: 'Australia Central',
      key: 'australiacentral',
    },
    australiacentral2: {
      provider: 'Azure',
      label: 'Australia Central 2',
      key: 'australiacentral2',
    },
    switzerlandnorth: {
      provider: 'Azure',
      label: 'Switzerland North',
      key: 'switzerlandnorth',
    },
    switzerlandwest: {
      provider: 'Azure',
      label: 'Switzerland West',
      key: 'switzerlandwest',
    },
    norwayeast: { provider: 'Azure', label: 'Norway East', key: 'norwayeast' },
    norwaywest: { provider: 'Azure', label: 'Norway West', key: 'norwaywest' },
    koreacentral: {
      provider: 'Azure',
      label: 'Korea Central',
      key: 'koreacentral',
    },
    koreasouth: { provider: 'Azure', label: 'Korea South', key: 'koreasouth' },
    chinaeast: { provider: 'Azure', label: 'China East', key: 'chinaeast' },
    chinanorth: { provider: 'Azure', label: 'China North', key: 'chinanorth' },
    germanynortheast: {
      provider: 'Azure',
      label: 'Germany Northeast',
      key: 'germanynortheast',
    },
    global: { provider: 'Azure', label: 'Global', key: 'global' },
  },
};

/** A region's full name, else its short name, else the code itself. */
export const regionFullName = (provider: string, code: string): string =>
  REGION_MAP[provider]?.[code]?.name ??
  REGION_MAP[provider]?.[code]?.label ??
  code;
